import { useState, useCallback, useEffect, useRef } from 'react';

const useChat = (userId) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const messagesEndRef = useRef(null);

  const sendMessage = useCallback(async (message) => {
    if (!message.trim()) return;

    // Get auth token from localStorage
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No authentication token found');
      return;
    }

    // Get current user
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const username = user.username || 'unknown';



    // Add user message to chat
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Get API URL from environment or use default
      const baseApiUrl = process.env.REACT_APP_API_URL || 'https://api.example.com';
      const chatApiUrl = `${baseApiUrl}/chat`;
      console.log('useChat - Base API URL:', baseApiUrl);
      console.log('useChat - Chat API URL being used:', chatApiUrl);
      console.log('useChat - process.env.REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
      
      const response = await fetch(chatApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: message,
          user_id: JSON.parse(localStorage.getItem('user'))?.username || 'unknown'
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid, redirect to login
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.reload();
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.response) {
        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString(),
          message_count: data.message_count,
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Handle error response
        const errorMessage = {
          role: 'assistant',
          content: `Error: ${data.error || 'Something went wrong'}`,
          timestamp: new Date().toISOString(),
          isError: true,
        };

        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true,
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Clear messages when user changes
  useEffect(() => {
    if (userId && userId !== currentUserId) {
      setMessages([]);
      setCurrentUserId(userId);
    }
  }, [userId, currentUserId]);

  const resetChat = useCallback(async () => {
    setMessages([]);
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const baseApiUrl = process.env.REACT_APP_API_URL || 'https://api.example.com';
      const chatApiUrl = `${baseApiUrl}/chat`;
      
      const response = await fetch(chatApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: '/reset',
          user_id: JSON.parse(localStorage.getItem('user'))?.username || 'unknown',
          reset_history: true
        }),
      });

      if (response.ok) {
        console.log('Chat history reset successfully');
      }
    } catch (error) {
      console.error('Error resetting chat:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const baseApiUrl = process.env.REACT_APP_API_URL || 'https://api.example.com';
      const historyApiUrl = `${baseApiUrl}/chat/history`;
      
      const response = await fetch(historyApiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.reload();
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.history) {
        // Convert history to message format
        const historyMessages = data.history.map((msg, index) => ({
          role: msg.role,
          content: msg.content,
          timestamp: new Date(Date.now() - (data.history.length - index) * 1000).toISOString(), // Approximate timestamps
        }));
        
        setMessages(historyMessages);
        console.log(`Loaded ${historyMessages.length} messages from history`);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  }, []);

  return {
    messages,
    sendMessage,
    isLoading,
    resetChat,
    messagesEndRef,
    clearMessages,
    loadHistory,
  };
};

export default useChat;
